const DB_NAME = "lenspro-ai-camera";
const DB_VERSION = 2;
const STORES = ["photos", "scans", "settings", "presets", "aiResults", "syncQueue", "projects", "checklists"];

let dbPromise;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      for (const storeName of STORES) {
        if (!db.objectStoreNames.contains(storeName)) {
          const store = db.createObjectStore(storeName, { keyPath: "id" });
          if (storeName === "photos" || storeName === "scans" || storeName === "aiResults") {
            store.createIndex("createdAt", "createdAt");
            store.createIndex("type", "type", { unique: false });
          }
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

async function tx(storeName, mode, operation) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const request = operation(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export const uid = (prefix = "item") => `${prefix}-${crypto.randomUUID()}`;

export async function putRecord(storeName, record) {
  return tx(storeName, "readwrite", (store) => store.put(record));
}

export async function getRecord(storeName, id) {
  return tx(storeName, "readonly", (store) => store.get(id));
}

export async function deleteRecord(storeName, id) {
  return tx(storeName, "readwrite", (store) => store.delete(id));
}

export async function clearStore(storeName) {
  return tx(storeName, "readwrite", (store) => store.clear());
}

export async function getAll(storeName) {
  return tx(storeName, "readonly", (store) => store.getAll());
}

export async function savePhoto({ blob, editedBlob, meta = {}, aiResults = [] }) {
  const now = new Date().toISOString();
  const record = {
    id: uid("photo"),
    type: meta.type || "photo",
    blob,
    editedBlob,
    aiResults,
    favorite: false,
    tags: meta.tags || [],
    album: meta.album || "Recents",
    name: meta.name || `LensPro ${new Date().toLocaleString()}`,
    filterId: meta.filterId || "none",
    createdAt: now,
    updatedAt: now,
    ...meta
  };
  await putRecord("photos", record);
  return record;
}

export async function saveScan(record) {
  const now = new Date().toISOString();
  const scan = {
    id: uid("scan"),
    type: record.type || "scan",
    title: record.title || "Untitled scan",
    text: record.text || "",
    blob: record.blob,
    structured: record.structured || {},
    createdAt: now,
    updatedAt: now,
    ...record
  };
  await putRecord("scans", scan);
  return scan;
}

export async function saveSetting(key, value) {
  await putRecord("settings", { id: key, value, updatedAt: new Date().toISOString() });
}

export async function getSetting(key, fallback) {
  const record = await getRecord("settings", key);
  return record?.value ?? fallback;
}

export async function estimateStorage() {
  if (!navigator.storage?.estimate) return null;
  return navigator.storage.estimate();
}
